import React, { useState, useEffect } from 'react'
import qs from 'query-string'
import { useMutation } from '@apollo/react-hooks'
import { useLocation } from 'react-router-dom'
import ReactSVG from 'react-svg'

import Input from '../../../../../components/form-fields/input'
import Button from '../../../../../components/button'
import Loader from '../../../../../components/loader'
import SHARE_LIST from '../../../../../../graphql/mutations/create-my-list-share.gql'
import checkIcon from '../../../../../../images/add-my-list-done.svg'
import { getGQLErrorMsg } from '../../../../../lib/apollo'
import { SHARE_LINK_EXPIRY_MESSAGE, shareListModes } from '../../../constants'
import styles from './share-list-modal.css'

/**
 * Share list modal
 *
 * Key features:
 * - Reads list context (`listId`) from modal query params.
 * - Requests a share URL when the component loads.
 * - Copies the share URL to clipboard when the user clicks "Copy Link".
 */

const ShareListModal = () => {
  const location = useLocation()
  const parsedSearch = qs.parse(location && location.search)
  const { listId, listType } = parsedSearch
  const [shareError, setShareError] = useState('')
  const [linkCopied, setLinkCopied] = useState(false)
  const [shareLink, setShareLink] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [shareList] = useMutation(SHARE_LIST)
  const isSaveDisabled = !listId || isLoading || !shareLink

  useEffect(() => {
    const shareListAsync = async () => {
      if (listId) {
        setIsLoading(true)
        try {
          const result = await shareList({
            variables: {
              input: {
                listType,
                listId,
                shareMode: shareListModes.snapshot
              }
            }
          })
          setShareLink(result?.data?.createMyListShare?.shareUrl || '')
        } catch (err) {
          setShareError(getGQLErrorMsg(err) || 'An error occurred while sharing the list. Please try again.')
        } finally {
          setIsLoading(false)
        }
      }
    }
    shareListAsync()
  }, [listId])

  const onShare = async () => {
    await navigator.clipboard.writeText(shareLink)
    setLinkCopied(true)
  }

  if (isLoading) {
    return (
      <div className={styles.wrapper}>
        <Loader />
      </div>
    )
  }

  if (linkCopied) {
    return (
      <div className={styles.wrapper}>
        <div className={styles.headerWrapper}>
          <div className={styles.iconButton}>
            <ReactSVG
              src={checkIcon}
              beforeInjection={(svg) => {
                svg.setAttribute('class', styles.icon)
              }}
            />
          </div>
          <span className={styles.header}>
            Link copied!
          </span>
          <span className={styles.subHeader}>
            Share with friends and family to show off your list.
          </span>
          <span className={styles.description}>
            {SHARE_LINK_EXPIRY_MESSAGE}
          </span>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.headerWrapper}>
        <span className={styles.header}>
          Your Share Link
        </span>
      </div>

      <div className={styles.inputWrapper}>
        <Input
          data-e2e="share-list-input"
          value={shareLink}
          readOnly
        />

        <Button
          wrapperClassName={styles.buttonWrapper}
          text="Copy Link"
          className={styles.button}
          onClick={onShare}
          disabled={isSaveDisabled}
        />
      </div>

      {shareError ? (
        <div className={styles.warning}>
          {shareError}
        </div>
      ) : (
        <div className={styles.description}>
          {SHARE_LINK_EXPIRY_MESSAGE}
        </div>
      )}
    </div>
  )
}

export default ShareListModal
